import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { useSafetyTranslation } from 'cng-common/features/translations'
import { NotificationController } from 'core-components/library/popup'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { getAttentionStatusRole } from 'partner360/entities/attentionStatuses'

import {
  CockpitPbmReplacementError,
  CockpitUserType,
  fetchCockpitPendingPbmReplacements,
  fetchCockpitTeamPbmEmails,
  requestCockpitPbmReplacement,
} from '../../../api'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../configs/translations'
import { isSameEmail, splitPersonName } from '../../../lib'
import {
  addCockpitAttentionPbmReplacements,
  setCockpitAttentionPbmReplacements,
} from '../../../model/financialCockpitSlice'
import {
  selectCockpitAttentionPbmReplacements,
  selectCockpitPendingActionsUsers,
  selectCockpitUserAccess,
} from '../../../model/selectors'
import type { IAttentionStatusesReminder } from '../types'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.replacement

/** Only a manager hands a partner to another PBM. */
export const useCanReplaceAttentionPbm = () => {
  const { role } = useSelector(selectCockpitUserAccess)

  return getAttentionStatusRole(role) === 'manager'
}

/** The manager's own PBMs, named as the cockpit knows them. */
export const useAttentionTeamPbms = (enabled: boolean) => {
  const user = useSelector(selectUser)
  const users = useSelector(selectCockpitPendingActionsUsers)
  const [emails, setEmails] = useState<string[] | null>(null)
  const managerEmail = user?.email ?? ''

  useEffect(() => {
    if (!enabled || !managerEmail) {
      return
    }

    let isCurrent = true
    fetchCockpitTeamPbmEmails(managerEmail)
      .then((next) => isCurrent && setEmails(next))
      .catch(() => isCurrent && setEmails([]))

    return () => {
      isCurrent = false
    }
  }, [enabled, managerEmail])

  const candidates = useMemo<IAttentionStatusesReminder[]>(
    () =>
      (emails ?? [])
        .map((email) => ({
          email,
          name:
            users.find(
              (candidate) =>
                candidate.UserType === CockpitUserType.PBM && isSameEmail(candidate.Email, email)
            )?.FullName || email,
        }))
        .sort((left, right) => left.name.localeCompare(right.name)),
    [emails, users]
  )

  return { candidates, isLoading: enabled && emails === null }
}

/** What waits for approval for the partners in view, read once they are shown. */
export const useAttentionPendingPbmReplacements = (partnerIds: string[], enabled: boolean) => {
  const dispatch = useAppDispatch()
  const replacements = useSelector(selectCockpitAttentionPbmReplacements)
  const key = partnerIds.join('|')

  useEffect(() => {
    if (!enabled || !key) {
      return
    }

    const ids = key.split('|')
    Promise.all(ids.map(fetchCockpitPendingPbmReplacements))
      .then((found) =>
        dispatch(
          setCockpitAttentionPbmReplacements({ partnerIds: ids, replacements: found.flat() })
        )
      )
      .catch(() => undefined)
  }, [dispatch, enabled, key])

  return useCallback(
    (partnerId: string, email: string) =>
      replacements.find(
        (replacement) =>
          replacement.partnerId === partnerId && isSameEmail(replacement.fromEmail, email)
      ),
    [replacements]
  )
}

interface IReplaceParams {
  partnerIds: string[]
  from: IAttentionStatusesReminder
  to: IAttentionStatusesReminder
}

/** Sends the change requests, one pair per partner, and tells how it went. */
export const useReplaceAttentionPbm = () => {
  const dispatch = useAppDispatch()
  const t = useSafetyTranslation()
  const user = useSelector(selectUser)
  const [isBusy, setBusy] = useState(false)

  const replace = useCallback(
    async ({ partnerIds, from, to }: IReplaceParams) => {
      const manager = {
        email: user?.email ?? '',
        firstName: user?.firstName ?? '',
        lastName: user?.lastName ?? '',
      }
      const next = splitPersonName(to.name, to.email)

      setBusy(true)

      const results = await Promise.allSettled(
        partnerIds.map((partnerId) =>
          requestCockpitPbmReplacement({ partnerId, fromEmail: from.email, to: next, manager })
        )
      )

      setBusy(false)

      const done = partnerIds.filter((_, index) => results[index].status === 'fulfilled')
      const failure = results.find(
        (result): result is PromiseRejectedResult => result.status === 'rejected'
      )

      dispatch(
        addCockpitAttentionPbmReplacements(
          done.map((partnerId) => ({ partnerId, fromEmail: from.email, toName: to.name }))
        )
      )

      if (done.length > 0) {
        NotificationController.showSuccess({ message: t(KEYS.success) })
      }

      if (failure) {
        const reason =
          failure.reason instanceof CockpitPbmReplacementError ? failure.reason.reason : null
        NotificationController.showError({ message: t(reason ? KEYS[reason] : KEYS.failure) })
      }

      return done.length > 0
    },
    [dispatch, t, user]
  )

  return { replace, isBusy }
}
